import { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Coffee, Target, Minimize2, Music, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const WORK_COMPLETE_QUOTES = [
  "Done is better than perfect. You showed up — that's the hardest part.",
  "You don't need motivation to be consistent. You just proved that.",
  "Progress isn't always visible, but it's always happening when you show up.",
  "Don't let perfect be the enemy of done. You just got something done.",
  "Small steps still move you forward. That session counted.",
  "Consistency beats intensity. One more session in the bank.",
  "You didn't feel like it, and you did it anyway. That's the real skill.",
  "Rest now. You earned it by doing the work, not by wanting to.",
  "The task doesn't have to be finished today. It just has to be started.",
  "Discipline is choosing between what you want now and what you want most.",
  "You're not behind. You're exactly where someone who just did the work should be.",
  "Messy progress beats perfect planning every single time.",
  "Your brain said 'not now' and you said 'yes, now.' That's growth.",
  "Motivation follows action, not the other way around. You just proved it.",
  "One session down. The mountain is smaller than it was 25 minutes ago.",
];

const BREAK_COMPLETE_QUOTES = [
  "Stretch. Breathe. You can get more done — but only if you rest first.",
  "Ready when you are. No rush, no guilt. Just the next step.",
  "Breaks aren't laziness. They're how your brain processes what you just did.",
  "You rested. Now you're sharper. That's not weakness, that's strategy.",
  "Back to it? Remember: you don't have to finish. Just start the next piece.",
  "The break is over, but the pressure isn't real. One thing at a time.",
  "You're not starting from zero. You're starting from experience.",
  "Pick the smallest next step. That's all you need right now.",
  "Consistency over intensity. Just one more round.",
  "Your future self is thanking you for not quitting after the last session.",
];

const getRandomQuote = (quotes) => quotes[Math.floor(Math.random() * quotes.length)];

const TIMER_PRESETS = [
  { label: '15 min', value: 15 },
  { label: '25 min', value: 25 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '60 min', value: 60 },
  { label: '90 min', value: 90 },
];

const MUSIC_PRESETS = [
  { id: 'none', name: 'No Music', url: null },
  { id: 'classical', name: 'Classical Focus', url: 'https://open.spotify.com/embed/playlist/2Jq7MgInM12HCALI9cvu5c?utm_source=generator&theme=0' },
  { id: 'lofi', name: 'Lo-fi Beats', url: 'https://open.spotify.com/embed/playlist/4bfj9Go9YnSq7L4YeWTWeY?utm_source=generator&theme=0' },
  { id: 'ambient', name: 'Ambient Flow', url: 'https://open.spotify.com/embed/playlist/1xgsBhIHE7cMKZ8hxz5sQN?utm_source=generator&theme=0' },
  { id: 'jazz', name: 'Jazz Study', url: 'https://open.spotify.com/embed/playlist/3cnkhyqinMpD5O6f6qh5l4?utm_source=generator&theme=0' },
  { id: 'custom', name: 'Custom Link', url: null },
];

// Convert regular Spotify/YouTube URLs to embeddable versions
function toEmbedUrl(rawUrl) {
  if (!rawUrl) return null;
  const url = rawUrl.trim();

  // Already an embed URL
  if (url.includes('/embed')) return url;

  // Spotify: open.spotify.com/playlist/ID or open.spotify.com/track/ID or open.spotify.com/album/ID
  const spotifyMatch = url.match(/open\.spotify\.com\/(playlist|track|album)\/([a-zA-Z0-9]+)/);
  if (spotifyMatch) {
    return `https://open.spotify.com/embed/${spotifyMatch[1]}/${spotifyMatch[2]}?utm_source=generator&theme=0`;
  }

  // YouTube: youtube.com/watch?v=ID or youtu.be/ID or youtube.com/playlist?list=ID
  const ytVideoMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytVideoMatch) {
    return `https://www.youtube.com/embed/${ytVideoMatch[1]}?autoplay=1`;
  }
  const ytPlaylistMatch = url.match(/youtube\.com\/playlist\?list=([a-zA-Z0-9_-]+)/);
  if (ytPlaylistMatch) {
    return `https://www.youtube.com/embed/videoseries?list=${ytPlaylistMatch[1]}&autoplay=1`;
  }

  // Return as-is if not recognized (user might paste a direct embed)
  return url;
}

function PomodoroTimer({ theme }) {
  const [mode, setMode] = useState('work');
  const [workDuration, setWorkDuration] = useState(() => {
    const saved = localStorage.getItem('pomodoroWorkDuration');
    return saved ? parseInt(saved) : 25;
  });
  const [breakDuration, setBreakDuration] = useState(() => {
    const saved = localStorage.getItem('pomodoroBreakDuration');
    return saved ? parseInt(saved) : 5;
  });
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = localStorage.getItem('pomodoroWorkDuration');
    return (saved ? parseInt(saved) : 25) * 60;
  });
  const [isRunning, setIsRunning] = useState(false);
  const [sessions, setSessions] = useState(() => {
    const saved = localStorage.getItem('pomodoroSessions');
    return saved ? parseInt(saved) : 0;
  });
  const [isPiP, setIsPiP] = useState(false);
  const [musicPreset, setMusicPreset] = useState(() => {
    const saved = localStorage.getItem('pomodoroMusicPreset');
    return saved || 'none';
  });
  const [customMusicUrl, setCustomMusicUrl] = useState(() => {
    const saved = localStorage.getItem('pomodoroCustomMusicUrl');
    return saved || '';
  });
  const [showMusicSettings, setShowMusicSettings] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const intervalRef = useRef(null);
  const pipWindowRef = useRef(null);

  // Request notification permission on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationsEnabled(permission === 'granted');
        });
      } else if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
      }
    }
  }, []);

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('pomodoroWorkDuration', workDuration.toString());
  }, [workDuration]);

  useEffect(() => {
    localStorage.setItem('pomodoroBreakDuration', breakDuration.toString());
  }, [breakDuration]);

  useEffect(() => {
    localStorage.setItem('pomodoroSessions', sessions.toString());
  }, [sessions]);

  useEffect(() => {
    localStorage.setItem('pomodoroMusicPreset', musicPreset);
  }, [musicPreset]);

  useEffect(() => {
    localStorage.setItem('pomodoroCustomMusicUrl', customMusicUrl);
  }, [customMusicUrl]);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleTimerComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning]);

  const handleTimerComplete = () => {
    setIsRunning(false);
    
    if (mode === 'work') {
      setSessions((prev) => prev + 1);
      const quote = getRandomQuote(WORK_COMPLETE_QUOTES);
      toast.success(quote, { duration: 8000 });
      
      // Browser notification
      if (notificationsEnabled && 'Notification' in window) {
        new Notification('Session Complete', {
          body: quote,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'pomodoro-complete',
          requireInteraction: false,
        });
      }
      
      setMode('break');
      setTimeLeft(breakDuration * 60);
    } else {
      const quote = getRandomQuote(BREAK_COMPLETE_QUOTES);
      toast.success(quote, { duration: 8000 });
      
      // Browser notification
      if (notificationsEnabled && 'Notification' in window) {
        new Notification('Break Over', {
          body: quote,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'pomodoro-complete',
          requireInteraction: false,
        });
      }
      
      setMode('work');
      setTimeLeft(workDuration * 60);
    }
  };

  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setMode('work');
    setTimeLeft(workDuration * 60);
  };

  const handleWorkDurationChange = (minutes) => {
    setWorkDuration(minutes);
    if (mode === 'work' && !isRunning) {
      setTimeLeft(minutes * 60);
    }
  };

  const openPiP = async () => {
    if (!document.pictureInPictureEnabled) {
      toast.error('Picture-in-Picture not supported in this browser');
      return;
    }

    try {
      const width = 320;
      const height = 180;
      const pipWindow = window.open(
        '',
        'TaskSplitterPiP',
        `width=${width},height=${height},toolbar=no,menubar=no,scrollbars=no`
      );

      if (pipWindow) {
        pipWindowRef.current = pipWindow;
        pipWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Task Splitter Timer</title>
              <style>
                body {
                  margin: 0;
                  padding: 16px;
                  font-family: 'Manrope', sans-serif;
                  background: ${theme?.colors.bg === 'bg-stone-50' ? '#fafaf9' : '#000'};
                  color: ${theme?.colors.textPrimary === 'text-stone-900' ? '#1c1917' : '#fff'};
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  height: 100vh;
                }
                .timer {
                  font-size: 48px;
                  font-weight: 600;
                  margin-bottom: 8px;
                }
                .mode {
                  font-size: 14px;
                  opacity: 0.7;
                }
                .sessions {
                  font-size: 12px;
                  margin-top: 8px;
                  opacity: 0.5;
                }
              </style>
            </head>
            <body>
              <div class="timer" id="timer"></div>
              <div class="mode" id="mode"></div>
              <div class="sessions" id="sessions"></div>
            </body>
          </html>
        `);
        pipWindow.document.close();
        setIsPiP(true);

        pipWindow.onbeforeunload = () => {
          setIsPiP(false);
          pipWindowRef.current = null;
        };
      }
    } catch (error) {
      console.error('PiP error:', error);
      toast.error('Failed to open Picture-in-Picture');
    }
  };

  useEffect(() => {
    if (isPiP && pipWindowRef.current) {
      const pipDoc = pipWindowRef.current.document;
      const timerEl = pipDoc.getElementById('timer');
      const modeEl = pipDoc.getElementById('mode');
      const sessionsEl = pipDoc.getElementById('sessions');

      if (timerEl) timerEl.textContent = formatTime(timeLeft);
      if (modeEl) modeEl.textContent = mode === 'work' ? 'Focus Time' : 'Break Time';
      if (sessionsEl) sessionsEl.textContent = `${sessions} sessions completed`;
    }
  }, [timeLeft, mode, sessions, isPiP]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = mode === 'work' 
    ? ((workDuration * 60 - timeLeft) / (workDuration * 60)) * 100
    : ((breakDuration * 60 - timeLeft) / (breakDuration * 60)) * 100;

  const getMusicUrl = () => {
    if (musicPreset === 'custom' && customMusicUrl) {
      return toEmbedUrl(customMusicUrl);
    }
    const preset = MUSIC_PRESETS.find(p => p.id === musicPreset);
    return preset?.url;
  };

  const musicUrl = getMusicUrl();

  return (
    <div 
      className={`${theme?.colors.surface || 'bg-white'} border ${theme?.colors.border || 'border-stone-200'} rounded-xl p-6 sm:p-8 shadow-sm`}
      data-testid="pomodoro-timer"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {mode === 'work' ? (
            <Target className={`w-6 h-6 ${theme?.colors.accent || 'text-[#E07A5F]'}`} />
          ) : (
            <Coffee className="w-6 h-6 text-[#81B29A]" />
          )}
          <h3 
            className={`text-xl font-medium ${theme?.colors.textPrimary || 'text-stone-900'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {mode === 'work' ? 'Focus Time' : 'Break Time'}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMusicSettings(!showMusicSettings)}
            className={`p-2 rounded-md hover:${theme?.colors.bg || 'bg-stone-100'} ${theme?.colors.textSecondary || 'text-stone-500'} transition-colors`}
            title="Music settings"
            data-testid="music-settings-btn"
          >
            <Music className="w-5 h-5" />
          </button>
          <button
            onClick={openPiP}
            className={`p-2 rounded-md hover:${theme?.colors.bg || 'bg-stone-100'} ${theme?.colors.textSecondary || 'text-stone-500'} transition-colors`}
            title="Picture-in-Picture"
            data-testid="pip-btn"
          >
            <Minimize2 className="w-5 h-5" />
          </button>
          <span 
            className={`text-sm ${theme?.colors.textSecondary || 'text-stone-500'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {sessions} sessions
          </span>
        </div>
      </div>

      {/* Music Settings */}
      {showMusicSettings && (
        <div className={`mb-6 p-4 ${theme?.colors.bg || 'bg-stone-50'} rounded-lg`}>
          <h4 className={`text-sm font-medium ${theme?.colors.textPrimary || 'text-stone-900'} mb-3`} style={{ fontFamily: 'Manrope, sans-serif' }}>
            Background Music
          </h4>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {MUSIC_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => setMusicPreset(preset.id)}
                data-testid={`music-preset-${preset.id}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  musicPreset === preset.id
                    ? `${theme?.colors.primary || 'bg-[#E07A5F]'} text-white`
                    : `${theme?.colors.surface || 'bg-white'} ${theme?.colors.textPrimary || 'text-stone-900'} border ${theme?.colors.border || 'border-stone-200'}`
                }`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {preset.name}
              </button>
            ))}
          </div>
          {musicPreset === 'custom' && (
            <div>
              <div className="flex items-center gap-2">
                <LinkIcon className={`w-4 h-4 ${theme?.colors.textSecondary || 'text-stone-400'}`} />
                <input
                  type="text"
                  value={customMusicUrl}
                  onChange={(e) => setCustomMusicUrl(e.target.value)}
                  placeholder="Paste Spotify or YouTube link..."
                  data-testid="custom-music-input"
                  className={`flex-1 px-3 py-2 border ${theme?.colors.border || 'border-stone-200'} rounded-lg text-sm bg-transparent`}
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: theme?.colors.rawText || '#1c1917' }}
                />
              </div>
              <p className={`text-xs ${theme?.colors.textSecondary || 'text-stone-400'} mt-2 ml-6`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Works with Spotify playlists, tracks, albums &amp; YouTube videos, playlists
              </p>
              {customMusicUrl && (
                <p className={`text-xs mt-1 ml-6 ${toEmbedUrl(customMusicUrl)?.includes('spotify') ? 'text-green-500' : toEmbedUrl(customMusicUrl)?.includes('youtube') ? 'text-red-400' : `${theme?.colors.textSecondary || 'text-stone-400'}`}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {toEmbedUrl(customMusicUrl)?.includes('spotify') ? 'Spotify detected' : toEmbedUrl(customMusicUrl)?.includes('youtube') ? 'YouTube detected' : 'Custom embed'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Music Player */}
      {musicUrl && isRunning && (
        <div className="mb-6 rounded-lg overflow-hidden">
          <iframe
            src={musicUrl}
            width="100%"
            height="152"
            frameBorder="0"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
            title="Background Music"
            className="rounded-lg"
            style={{ border: 'none' }}
          ></iframe>
        </div>
      )}

      {/* Timer Presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TIMER_PRESETS.map((preset) => (
          <button
            key={preset.value}
            onClick={() => handleWorkDurationChange(preset.value)}
            disabled={isRunning}
            data-testid={`timer-preset-${preset.value}`}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors disabled:opacity-50 ${
              workDuration === preset.value
                ? `${theme?.colors.primary || 'bg-[#E07A5F]'} text-white`
                : `${theme?.colors.surface || 'bg-white'} border ${theme?.colors.border || 'border-stone-200'} ${theme?.colors.textPrimary || 'text-stone-900'}`
            }`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Timer Display */}
      <div className="text-center mb-6">
        <div 
          className="text-6xl sm:text-7xl font-medium mb-4"
          style={{ fontFamily: 'Manrope, sans-serif' }}
          data-testid="timer-display"
        >
          <span className={mode === 'work' ? (theme?.colors.accent || 'text-[#E07A5F]') : 'text-[#81B29A]'}>
            {formatTime(timeLeft)}
          </span>
        </div>
        
        {/* Progress Ring */}
        <div className="relative w-32 h-32 mx-auto mb-6">
          <svg className="transform -rotate-90 w-32 h-32">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="transparent"
              className={theme?.colors.textSecondary || 'text-stone-300'}
              opacity="0.3"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - progress / 100)}`}
              className={mode === 'work' ? (theme?.colors.accent || 'text-[#E07A5F]') : 'text-[#81B29A]'}
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span 
              className={`text-2xl font-medium ${theme?.colors.textPrimary || 'text-stone-900'}`}
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {Math.round(progress)}%
            </span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={toggleTimer}
          data-testid="timer-toggle-btn"
          className={`${
            mode === 'work' ? (theme?.colors.primary + ' ' + theme?.colors.primaryHover || 'bg-[#E07A5F] hover:bg-[#D46B4E]') : 'bg-[#81B29A] hover:bg-[#70A189]'
          } text-white px-8 py-3 rounded-lg font-medium transition-colors inline-flex items-center gap-2`}
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {isRunning ? (
            <>
              <Pause className="w-5 h-5" />
              Pause
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Start
            </>
          )}
        </button>

        <button
          onClick={resetTimer}
          data-testid="timer-reset-btn"
          className={`p-3 hover:${theme?.colors.bg || 'bg-stone-100'} rounded-lg ${theme?.colors.textSecondary || 'text-stone-500'} transition-colors`}
          title="Reset timer"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      <p 
        className={`text-sm ${theme?.colors.textSecondary || 'text-stone-400'} text-center mt-4`}
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        {mode === 'work' ? `${workDuration} minutes of focused work` : `${breakDuration} minutes to recharge`}
      </p>
    </div>
  );
}

export default PomodoroTimer;